import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TInputSelectInstance } from 'widgets/forms/InputSelect';

type TValidationConfig = {
    patterns?: { parse?: string };
    errors?: { minLength?: string; maxLength?: string; [key: string]: unknown };
    [key: string]: unknown;
};

/**
 * @param AddressForm Base widget for extending
 * @returns AreaCodeMixin class with area code parsing, validation sync, and country change handling
 */
function AreaCodeMixinClassCreator(AddressForm: ReturnType<typeof import('widgets/forms/AddressForm').default>) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class AreaCodeMixin
     * @augments AddressForm
     * @classdesc Mixin that adds area code handling to address forms:
     * 1. Parses combined phone values (e.g. "+14165551234") into separate area code and phone fields
     * 2. Updates phone validation pattern dynamically when area code changes
     */
    class AreaCodeMixin extends AddressForm {
        minLengthMsgTemplate: string | undefined;
        maxLengthMsgTemplate: string | undefined;

        private getPhoneFieldId(): string | undefined {
            return this.getAddressFields().find(id => id.includes('phone'));
        }

        private getAreaCodeFieldId(): string | undefined {
            return this.getAddressFields().find(id => id.includes('areaCode'));
        }

        private getSelectElement(field: TInputSelectInstance): HTMLSelectElement | null {
            return field.ref('field').get() as HTMLSelectElement || null;
        }

        init() {
            super.init();

            const areaCodeID = this.getAreaCodeFieldId();

            if (areaCodeID) {
                this.getById(areaCodeID, (areaCodeField: TInputSelectInstance) => {
                    this.setupCodeDisplay(areaCodeField);
                });
            }

            const phoneID = this.getPhoneFieldId();

            if (!phoneID) {
                return;
            }

            this.getById(phoneID, (phoneField: TBasicInputInstance) => {
                const phoneValue = phoneField.getValue();

                if (phoneValue) {
                    this.parsePhoneAreaCode({
                        phone: phoneValue,
                        countryCode: String(this.config.countryCode || '')
                    });
                } else {
                    this.applySelectedAreaCodeValidation(phoneField);
                }
            });
        }

        private setupCodeDisplay(areaCodeField: TInputSelectInstance) {
            const selectEl = this.getSelectElement(areaCodeField);

            if (!selectEl) {
                return;
            }

            const bSelectWrapper = selectEl.closest('.b-select');

            if (!bSelectWrapper) {
                return;
            }

            bSelectWrapper.classList.add('m-code_select');

            const codeDisplay = document.createElement('span');

            codeDisplay.className = 'b-select-code_display';
            codeDisplay.setAttribute('aria-hidden', 'true');
            codeDisplay.textContent = selectEl.value;

            bSelectWrapper.insertBefore(codeDisplay, selectEl);

            selectEl.addEventListener('change', () => {
                codeDisplay.textContent = selectEl.value;
            });
        }

        private static updateCodeDisplay(selectEl: HTMLSelectElement) {
            const codeDisplay = selectEl
                .closest('.b-select')
                ?.querySelector<HTMLElement>('.b-select-code_display');

            if (codeDisplay) {
                codeDisplay.textContent = selectEl.value;
            }
        }

        /**
         * @description Applies validation pattern and min/max length from the
         * currently selected area code option to the phone field.
         */
        private applySelectedAreaCodeValidation(phoneField: TBasicInputInstance) {
            const areaCodeID = this.getAreaCodeFieldId();

            if (!areaCodeID) {
                return;
            }

            this.getById(areaCodeID, (areaCodeField: TInputSelectInstance) => {
                const selectedOption = areaCodeField.getSelectedOptions()?.get(0);

                if (!selectedOption) {
                    return;
                }

                const pattern = selectedOption.getAttribute('data-validation');
                const minLen = selectedOption.getAttribute('data-min-length');
                const maxLen = selectedOption.getAttribute('data-max-length');

                if (pattern) {
                    this.applyValidationPattern(phoneField, pattern, minLen, maxLen, false);
                }
            });
        }

        fillAddressFields(dataset: Record<string, string>) {
            super.fillAddressFields(dataset);
            this.parsePhoneAreaCode(dataset);
        }

        /**
         * @description Parses a combined phone value (e.g. "+14165551234") into
         * separate areaCode select and phone input values using country-aware matching
         * with a longest-match fallback.
         */
        parsePhoneAreaCode(dataset: Record<string, string>) {
            if (dataset.areaCode || !dataset.phone || !dataset.phone.startsWith('+')) {
                return;
            }

            const phone = dataset.phone;
            const rawCountry = dataset.countryCode as unknown;
            const countryCode = (rawCountry && typeof rawCountry === 'object')
                ? (rawCountry as { value: string }).value
                : (rawCountry as string || '');

            const phoneID = this.getPhoneFieldId();
            const areaCodeID = this.getAreaCodeFieldId();

            if (!phoneID || !areaCodeID) {
                return;
            }

            this.getById(areaCodeID, (areaCodeField: TInputSelectInstance) => {
                const selectEl = this.getSelectElement(areaCodeField);

                if (!selectEl) {
                    return;
                }

                const options = Array.from(selectEl.options).filter(opt => opt.value);
                let matched: HTMLOptionElement | null | undefined = null;

                // Country-aware match: use address country to find the exact area code option.
                // Needed because some countries share the same code (e.g. +1 = US and CA).
                if (countryCode) {
                    const countryOption = options.find(
                        opt => (opt.dataset.country || '').toUpperCase() === countryCode.toUpperCase()
                    );

                    if (countryOption && phone.startsWith(countryOption.value)) {
                        matched = countryOption;
                    }
                }

                // Longest-match fallback: if country didn't help (unknown or mismatch),
                // try all options sorted by code length descending to avoid partial matches
                // (e.g. +1 vs +1242 — longer wins first).
                if (!matched) {
                    const sorted = [...options].sort(
                        (a, b) => b.value.length - a.value.length
                    );

                    matched = sorted.find(opt => phone.startsWith(opt.value));
                }

                // If we found a match, set the area code select 
                // and update the phone field to only the remaining part.
                // apply a new validation pattern if defined on the matched option.
                if (matched) {
                    selectEl.selectedIndex = matched.index;
                    AreaCodeMixin.updateCodeDisplay(selectEl);

                    const pattern = matched.getAttribute('data-validation');
                    const minLen = matched.getAttribute('data-min-length');
                    const maxLen = matched.getAttribute('data-max-length');

                    this.getById(phoneID, (phoneField: TBasicInputInstance) => {
                        phoneField.setValue(phone.slice(matched!.value.length), true);

                        if (pattern) {
                            this.applyValidationPattern(phoneField, pattern, minLen, maxLen, false);
                        }
                    });
                }
            });
        }

        /**
         * @description Handles area code dropdown change — updates phone field validation
         * pattern from the selected option's data-validation attribute.
         * Wired via data-forward-to-parent in dynamicForms.js.
         */
        handleAreaCodeChange(areaCodeField: TInputSelectInstance) {
            const selectedOption = areaCodeField.getSelectedOptions()?.get(0);
            const newPattern = selectedOption?.getAttribute('data-validation');

            if (!newPattern) {
                return;
            }

            const phoneID = this.getPhoneFieldId();

            if (!phoneID) {
                return;
            }

            const minLen = selectedOption?.getAttribute('data-min-length');
            const maxLen = selectedOption?.getAttribute('data-max-length');

            this.getById(phoneID, (phoneField: TBasicInputInstance) => {
                this.applyValidationPattern(phoneField, newPattern, minLen, maxLen, true);
            });
        }

        /**
         * @description Applies a validation regex pattern and min/max length
         * to the phone field, optionally triggers validation.
         */
        private applyValidationPattern(
            phoneField: TBasicInputInstance,
            pattern: string,
            minLength?: string | null,
            maxLength?: string | null,
            validate?: boolean
        ) {
            const currentValidation = phoneField.data('validationConfig') as TValidationConfig;

            if (!currentValidation || !currentValidation.patterns) {
                return;
            }

            let configChanged = false;

            if (currentValidation.patterns.parse !== pattern) {
                currentValidation.patterns.parse = pattern;
                configChanged = true;
            }

            const errors = currentValidation.errors;

            if (errors) {
                if (!this.minLengthMsgTemplate && errors.minLength) {
                    this.minLengthMsgTemplate = errors.minLength;
                }

                if (!this.maxLengthMsgTemplate && errors.maxLength) {
                    this.maxLengthMsgTemplate = errors.maxLength;
                }

                if (this.minLengthMsgTemplate && minLength) {
                    errors.minLength = this.minLengthMsgTemplate.replace('{minlength}', minLength);
                    configChanged = true;
                }

                if (this.maxLengthMsgTemplate && maxLength) {
                    errors.maxLength = this.maxLengthMsgTemplate.replace('{maxlength}', maxLength);
                    configChanged = true;
                }
            }

            if (configChanged) {
                phoneField.setPref('validationConfig', currentValidation);
            }

            const inputEl = phoneField.ref('field').get() as HTMLInputElement;

            if (inputEl) {
                if (minLength) {
                    inputEl.minLength = parseInt(minLength, 10);
                }

                if (maxLength) {
                    inputEl.maxLength = parseInt(maxLength, 10);
                }
            }

            if (validate && phoneField.getValue()) {
                phoneField.validate();
            }
        }
    }

    return AreaCodeMixin;
}

export type TAreaCodeMixin = ReturnType<typeof AreaCodeMixinClassCreator>;
export type TAreaCodeMixinInstance = InstanceType<TAreaCodeMixin>;
export default AreaCodeMixinClassCreator;
